import type { EquipeRow, RowErrors } from "@/shared/components/equipe-obra/equipe-obra.types";
import {
  EQUIPE_FIELD_REQUIRED_MESSAGE,
  EQUIPE_OVERLAP_MESSAGE,
  temSobreposicao,
  validarPeriodoAlocacao,
} from "@/modules/recursos/equipe-obra.rules";
import { calcularCronograma, readDuracao } from "./nova-obra.duracao";

export type EquipeLinhaErros = RowErrors;

/**
 * Alocação da Etapa 4 "Equipe" (HU 2530), só no cliente (R3): a obra ainda não existe, então
 * nada é gravado aqui — o POST da 2543 envia `{ papelObraId, usuarioId, inicio, fim }`.
 * O formato é o da linha de Definir Equipe de Obra (HU 2050), a mesma linha de tela.
 */
export type EquipeLinha = EquipeRow;

/** Snapshot da Etapa 4 em `state.etapas.equipe`. */
export interface DadosEquipe {
  linhas: EquipeLinha[];
  /**
   * Justificativa dos diálogos de conflito/indisponibilidade de Recursos (HU 2530 RN08), dada no
   * último "Próxima etapa"; vai no POST como `justificativaEquipe`. Mexer nas linhas a descarta.
   */
  justificativa?: string | null;
}

/** `justificativaEquipe` do POST (nota da PO: justificativas da Nova Obra têm até 500). */
export const JUSTIFICATIVA_EQUIPE_MAX = 500;

export const EQUIPE_INICIAL: DadosEquipe = { linhas: [], justificativa: null };

export const EQUIPE_LINHA_EM_EDICAO_MESSAGE = "Confirme ou cancele a alocação em edição.";

export interface PeriodoWizard {
  inicio: string;
  fim: string;
}

/**
 * Período da obra definido no wizard (Etapa 2, Impasse 21): do Início da Obra ao Término
 * previsto. `null` enquanto a Duração não permite calcular o término.
 */
export function periodoDaObraNoWizard(duracao: unknown): PeriodoWizard | null {
  const cronograma = calcularCronograma(readDuracao(duracao));
  if (!cronograma.inicio || !cronograma.terminoPrevisto) return null;
  return { inicio: cronograma.inicio, fim: cronograma.terminoPrevisto };
}

/** Lê o snapshot guardado (`unknown` no store); formato inesperado vira a equipe vazia. */
export function readEquipe(raw: unknown): DadosEquipe {
  if (typeof raw !== "object" || raw === null) return EQUIPE_INICIAL;

  const { linhas, justificativa } = raw as { linhas?: unknown; justificativa?: unknown };
  return {
    justificativa: typeof justificativa === "string" ? justificativa : null,
    linhas: Array.isArray(linhas)
      ? linhas.filter(
          (linha): linha is EquipeLinha =>
            typeof linha === "object" && linha !== null && typeof linha.id === "string",
        )
      : [],
  };
}

export const isEquipeVazia = (dados: DadosEquipe) => dados.linhas.length === 0;

/** Erros em tela de uma alocação: datas contra o período do wizard, profissional e sobreposição. */
export function errosDaLinha(
  linha: EquipeLinha,
  linhas: readonly EquipeLinha[],
  periodo: PeriodoWizard,
): EquipeLinhaErros {
  const errors: EquipeLinhaErros = { ...validarPeriodoAlocacao(linha, periodo) };

  if (!linha.usuarioId) {
    errors.usuario = EQUIPE_FIELD_REQUIRED_MESSAGE;
  } else if (
    temSobreposicao(
      linha,
      linhas.filter((outra) => outra.id !== linha.id),
    )
  ) {
    errors.usuario = EQUIPE_OVERLAP_MESSAGE;
  }

  return errors;
}

/** A alocação tem algum erro em tela (profissional, Data Início ou Data Fim). */
export const linhaTemErros = (errors: EquipeLinhaErros) => Object.values(errors).some(Boolean);

/** Chave de erro por alocação: `alocacao.<id>`. */
export const chaveErroAlocacao = (id: string) => `alocacao.${id}`;

/**
 * Erros da etapa (AC7): sem período definido na Duração não há contra o que validar; cada
 * alocação precisa de profissional, datas dentro do período do wizard e sem sobreposição, e
 * nenhuma pode estar em edição. Papel obrigatório ausente não bloqueia: vira o aviso
 * "Equipe incompleta" (RN03/RN04) na própria etapa. Equipe vazia é permitida.
 */
export function validateEtapaEquipe(
  dados: DadosEquipe,
  periodo: PeriodoWizard | null,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (dados.linhas.length === 0) return errors;

  if (!periodo) {
    errors.periodo = "Defina o período da obra na etapa Duração.";
    return errors;
  }

  for (const linha of dados.linhas) {
    if (linha.editing) {
      errors[chaveErroAlocacao(linha.id)] = EQUIPE_LINHA_EM_EDICAO_MESSAGE;
      continue;
    }
    const erros = errosDaLinha(linha, dados.linhas, periodo);
    const primeiro = erros.usuario ?? erros.inicio ?? erros.fim;
    if (primeiro) errors[chaveErroAlocacao(linha.id)] = primeiro;
  }

  return errors;
}

const PREFIXO_CONFLITO = "Conflito: ";
const PREFIXO_INDISPONIBILIDADE = "\nIndisponibilidade: ";

/**
 * Limite de cada diálogo de verificação (conflito / indisponibilidade). Com os dois no mesmo
 * avanço, as justificativas viajam juntas em `justificativaEquipe` (até 500): o conflito fica com
 * metade do que sobra após os prefixos e a indisponibilidade com o restante, para a junção nunca
 * cortar texto digitado. Mesma divisão de Recursos (HU 2059), com o limite do POST da Nova Obra.
 */
export function limiteJustificativaEquipe(
  etapa: "conflitos" | "indisponivel",
  ambas: boolean,
  conflitoUsado = 0,
): number {
  if (!ambas) return JUSTIFICATIVA_EQUIPE_MAX;
  const livre =
    JUSTIFICATIVA_EQUIPE_MAX - PREFIXO_CONFLITO.length - PREFIXO_INDISPONIBILIDADE.length;
  return etapa === "conflitos" ? Math.floor(livre / 2) : livre - conflitoUsado;
}

/** Uma justificativa por POST: com conflito e indisponibilidade, as duas vão juntas e identificadas. */
export function combinarJustificativasEquipe({
  conflito,
  indisponibilidade,
}: {
  conflito?: string;
  indisponibilidade?: string;
}): string | null {
  if (conflito && indisponibilidade) {
    return `${PREFIXO_CONFLITO}${conflito}${PREFIXO_INDISPONIBILIDADE}${indisponibilidade}`.slice(
      0,
      JUSTIFICATIVA_EQUIPE_MAX,
    );
  }
  return conflito || indisponibilidade || null;
}
